/**
 * Bell + unread badge for the shell header — the app previously had no
 * passive notification indicator anywhere outside NotificationsPage.tsx/
 * ProfilePage.tsx's own compact section. Matches the Flutter app's own
 * fetch-once behaviour (main_shell_layout.dart) rather than polling: no
 * `refetchInterval` here, same as every other query in this codebase.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Trash2 } from 'lucide-react';
import { deleteNotification, listNotifications, markAllAsRead } from '../api/endpoints/notifications';

export function NotificationBell(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const query = useQuery({
    queryKey: ['notifications', { page: 1 }],
    queryFn: () => listNotifications({ page: 1 }),
  });

  const notifications = query.data?.results ?? [];
  const unread = notifications.filter((notification) => !notification.is_read).length;

  const markAll = useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteNotification(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  return (
    <div className="notification-bell">
      <button
        type="button"
        className="notification-bell__trigger"
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
        aria-expanded={open}
        onClick={() => setOpen((previous) => !previous)}
      >
        <Bell size={20} aria-hidden="true" />
        {unread > 0 && <span className="notification-bell__badge">{unread}</span>}
      </button>

      {open && (
        <div className="notification-bell__panel">
          <div className="notification-bell__panel-header">
            <span>Notifications</span>
            {unread > 0 && (
              <button type="button" disabled={markAll.isPending} onClick={() => markAll.mutate()}>
                Mark all as read
              </button>
            )}
          </div>

          {notifications.length === 0 && <p className="notification-bell__empty">No notifications.</p>}

          {notifications.length > 0 && (
            <ul className="notification-bell__list">
              {notifications.slice(0, 8).map((notification) => (
                <li
                  key={notification.id}
                  className={
                    notification.is_read
                      ? 'notification-bell__item'
                      : 'notification-bell__item notification-bell__item--unread'
                  }
                >
                  <p className="notification-bell__item-title">{notification.title}</p>
                  <p className="notification-bell__item-message">{notification.message}</p>
                  <button
                    type="button"
                    className="notification-bell__item-delete"
                    aria-label={`Delete notification: ${notification.title}`}
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(notification.id)}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
